import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import {
  Add as AddIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';

// Import types
import { LocationConfig } from './types/AccuLynxTypes';

// Import custom hooks
import { useAccuLynxData } from './hooks/useAccuLynxData';
import { useAccuLynxActions } from './hooks/useAccuLynxActions';

// Import components
import { LocationCard } from './LocationCard';
// import { HelpPanel } from './HelpPanel';
import { LoadingSpinner } from './LoadingSpinner';
import { HeaderSection } from './HeaderSection';
import { GetStartedSection } from './GetStartedSection';
import { ConfirmationModal } from './ConfirmationModal';

// Import styled components
import { StyledForm, Section } from './styles/AccuLynxStyles';
import { INTEGRATIONS_API_BASE } from 'application/config/apiConfig';
import { useNavigate } from 'react-router-dom';

export const AccuLynxSetup: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();

  // Custom hooks for data and actions
  const {
    isLoading,
    isLoadingCircles,
    isLoadingStages,
    circles,
    paivStages,
    config,
    isEditing,
    savedConfig,
    setConfig,
    setIsEditing,
    setSavedConfig,
    stageCategories,
    circleLevels,
    hideCrmCategory,
  } = useAccuLynxData();

  const hasIntegration = savedConfig.locations.length > 0;

  const {
    isSubmitting,
    submitStatus,
    addLocation,
    removeLocation,
    updateLocation,
    handleApiKeyChange,
    updateStageMapping,
    validateConfig,
    handleSubmit,
    handleCancel,
    handleGetStarted,
    setSubmitStatus,
  } = useAccuLynxActions(
    config,
    setConfig,
    paivStages,
    circles,
    isEditing,
    savedConfig,
    setIsEditing,
    setSavedConfig,
    stageCategories
  );

  // If locations were pre-populated with a single blank entry and nothing saved, auto-enable edit mode
  useEffect(() => {
    if (!isEditing) {
      const hasSaved = savedConfig.locations.length > 0;
      const hasOnlyBlank =
        config.locations.length === 1 &&
        !config.locations[0]?.id &&
        !config.locations[0]?.circleId &&
        !config.locations[0]?.apiKey;
      if (!hasSaved && hasOnlyBlank) {
        setIsEditing(true);
      }
    }
  }, [isEditing, config.locations, savedConfig.locations, setIsEditing]);

  // Turn-off flow state
  const [isTurningOff, setIsTurningOff] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmText?: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: () => {},
  });

  // Turn off AccuLynx for this org by sending empty locations
  const turnOffIntegration = async (): Promise<void> => {
    setConfirmationModal({
      open: true,
      title: 'Turn Off AccuLynx',
      message:
        'Are you sure you want to turn off AccuLynx for this organization?\n\nThis will:\n• Remove all AccuLynx integrations\n• Unsubscribe from all webhooks\n• Delete all stage mappings\n• This action cannot be undone',
      confirmText: 'Turn Off',
      onConfirm: async () => {
        setConfirmationModal((prev) => ({ ...prev, open: false }));
        await performTurnOff();
      },
    });
  };

  // Actually perform the turn off operation
  const performTurnOff = async (): Promise<void> => {
    try {
      setIsTurningOff(true);
      const orgID = (window as any).me.orgID;
      const payload = {
        orgID,
        locations: [],
        userId: (window as any).me.userID,
      };

      const res = await fetch(
        `${INTEGRATIONS_API_BASE}/setup-acculynx-integration`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any).me.accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json();
      if (!res.ok || !result?.success)
        throw new Error(result?.error || 'Failed to turn off AccuLynx');

      // Clear local state completely
      setConfig({ locations: [], stageMappings: [] });
      setSavedConfig({ locations: [], stageMappings: [] });
      setIsEditing(false);
      setSubmitStatus({
        type: 'success',
        message: result.message || 'AccuLynx turned off',
      });
    } catch (e: any) {
      setSubmitStatus({
        type: 'error',
        message: e?.message || 'Failed to turn off AccuLynx',
      });
    } finally {
      setIsTurningOff(false);
    }
  };

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {isLoading || isLoadingCircles || isLoadingStages ? (
        <LoadingSpinner />
      ) : (
        <>
          <HeaderSection
            isEditing={isEditing}
            hasIntegration={hasIntegration}
            isSubmitting={isSubmitting}
            validateConfig={validateConfig}
            onSubmit={handleSubmit}
            onEdit={() =>
              setConfirmationModal({
                open: true,
                title: 'Edit Configuration',
                message:
                  'Are you sure you want to edit this integration?\n\nYou will be able to change locations, API keys, and stage mappings.',
                confirmText: 'Edit',
                onConfirm: () => {
                  setConfirmationModal((prev) => ({ ...prev, open: false }));
                  setIsEditing(true);
                },
              })
            }
            onCancel={handleCancel}
          />

          {submitStatus.type && (
            <Alert
              severity={submitStatus.type}
              sx={{ mb: 3 }}
              onClose={() => setSubmitStatus({ type: undefined, message: '' })}
            >
              {submitStatus.message}
            </Alert>
          )}

          {!hasIntegration && !isEditing ? (
            <GetStartedSection onGetStarted={handleGetStarted} />
          ) : (
            <StyledForm onSubmit={handleSubmit}>
              <Section>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">Locations</Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<HelpOutlineIcon />}
                      onClick={() =>
                        navigate('/settings/integrations/acculynx/help')
                      }
                    >
                      Help
                    </Button>
                  </Box>
                  {isEditing && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={addLocation}
                      startIcon={<AddIcon />}
                    >
                      Add Location
                    </Button>
                  )}
                </Box>

                {/* Render location cards */}
                {config.locations.map(
                  (location: LocationConfig, locationIndex: number) => (
                    <LocationCard
                      key={locationIndex}
                      location={location}
                      locationIndex={locationIndex}
                      isEditing={isEditing}
                      isLoadingCircles={isLoadingCircles}
                      isLoadingStages={isLoadingStages}
                      circles={circles}
                      paivStages={paivStages}
                      stageCategories={stageCategories}
                      circleLevels={circleLevels}
                      hideCrmCategory={hideCrmCategory}
                      onUpdateLocation={updateLocation}
                      onApiKeyChange={handleApiKeyChange}
                      onRemoveLocation={removeLocation}
                      onUpdateStageMapping={updateStageMapping}
                    />
                  )
                )}
              </Section>

              {isEditing && hasIntegration && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 3,
                  }}
                >
                  <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Box>
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ mr: 2 }}
                      onClick={turnOffIntegration}
                      disabled={isTurningOff}
                    >
                      {isTurningOff ? 'Turning off...' : 'Turn off AccuLynx'}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || !validateConfig()}
                    >
                      {isSubmitting
                        ? 'Saving Integration...'
                        : 'Save Integration'}
                    </Button>
                  </Box>
                </Box>
              )}
            </StyledForm>
          )}

          {/* Removed top-right Help button */}

          {/* Confirmation Modal */}
          <ConfirmationModal
            open={confirmationModal.open}
            title={confirmationModal.title}
            message={confirmationModal.message}
            onConfirm={confirmationModal.onConfirm}
            onCancel={() =>
              setConfirmationModal((prev) => ({ ...prev, open: false }))
            }
            confirmText={confirmationModal.confirmText || 'Confirm'}
            cancelText="Cancel"
            severity="warning"
          />

          {/* HelpPanel removed as per edit hint */}
        </>
      )}
    </Box>
  );
};
